/**
 * Atleta em uma malha só (item 3.2): o corpo do LOD pedido e as peças visíveis (cabelo, barba, olhos,
 * sobrancelhas, óculos e acessório) viram uma única SkinnedMesh com um material. Cada vértice leva
 * `aPart`, que diz ao shader de onde vem a cor. Assim cada atleta custa 1 chamada de desenho
 * (mais 1 do contorno e 1 da sombra), em vez de uma por peça.
 */
import * as THREE from "three";

/** De onde vem a cor de cada vértice. A ordem é usada no shader (`vPart`). */
export const PART = {
  /** Pele e uniforme pelos campos de COLOR_0. */
  body: 0,
  /** Cabelo, barba e sobrancelhas. */
  hair: 1,
  /** Olhos. */
  ink: 2,
  /** Óculos de voo: R = lente, B = alça. */
  goggles: 3,
  /** Acessório com contorno (braçadeira, capacete, taco): R = principal, G = secundária, B = fixa. */
  accessory: 4,
  /** Capa: como o acessório, mas sem contorno (é uma folha só). */
  cape: 5,
} as const;
export type Part = (typeof PART)[keyof typeof PART];

export interface Piece {
  mesh: THREE.SkinnedMesh;
  part: Part;
}

/** Esqueleto comum a todas as malhas de um modelo: a mesma ordem de ossos serve a todos os LODs. */
export interface ModelSkeleton {
  bones: string[];
  boneInverses: THREE.Matrix4[];
  index: Map<string, number>;
}

/** Junta as listas de juntas de todas as malhas com pele do modelo. */
export function modelSkeleton(scene: THREE.Object3D): ModelSkeleton {
  const skeleton: ModelSkeleton = { bones: [], boneInverses: [], index: new Map() };
  scene.traverse((o) => {
    if (!(o instanceof THREE.SkinnedMesh)) return;
    o.skeleton.bones.forEach((b, k) => {
      if (skeleton.index.has(b.name)) return;
      skeleton.index.set(b.name, skeleton.bones.length);
      skeleton.bones.push(b.name);
      skeleton.boneInverses.push((o.skeleton.boneInverses[k] ?? new THREE.Matrix4()).clone());
    });
  });
  return skeleton;
}

export interface MergedAthlete {
  geometry: THREE.BufferGeometry;
  /** Nome de cada morph, na ordem de `morphAttributes.position`. */
  morphs: string[];
}

/**
 * Funde as malhas num só BufferGeometry. Todas as peças vêm do mesmo GLB, com transformação identidade
 * sob a mesma armadura; cada uma tem sua lista de juntas, então os índices são remapeados pelo nome do osso.
 */
export function mergeAthlete(
  body: THREE.SkinnedMesh,
  pieces: readonly Piece[],
  skeleton: ModelSkeleton,
): MergedAthlete {
  const all: Piece[] = [{ mesh: body, part: PART.body }, ...pieces];
  const morphs = Object.entries(body.morphTargetDictionary ?? {})
    .sort((a, b) => a[1] - b[1])
    .map(([name]) => name);

  let vertexCount = 0;
  let indexCount = 0;
  for (const { mesh } of all) {
    vertexCount += mesh.geometry.getAttribute("position").count;
    indexCount += mesh.geometry.index?.count ?? mesh.geometry.getAttribute("position").count;
  }
  const position = new Float32Array(vertexCount * 3);
  const normal = new Float32Array(vertexCount * 3);
  const color = new Float32Array(vertexCount * 3);
  const skinIndex = new Uint16Array(vertexCount * 4);
  const skinWeight = new Float32Array(vertexCount * 4);
  const part = new Float32Array(vertexCount);
  const morphDeltas = morphs.map(() => new Float32Array(vertexCount * 3));
  const index = vertexCount > 65535 ? new Uint32Array(indexCount) : new Uint16Array(indexCount);

  let v0 = 0;
  let i0 = 0;
  for (const { mesh, part: p } of all) {
    const g = mesh.geometry;
    const pos = g.getAttribute("position");
    const nor = g.getAttribute("normal");
    const col = g.getAttribute("color");
    const ji = g.getAttribute("skinIndex");
    const jw = g.getAttribute("skinWeight");
    const remap = mesh.skeleton.bones.map((b) => skeleton.index.get(b.name) ?? 0);
    // Cada malha vem quantizada com escala e deslocamento próprios, embutidos nas matrizes inversas de
    // ligação. Para dividir o esqueleto, leva o vértice ao espaço da ligação comum:
    // v' = comum⁻¹ · própria · v  (o mesmo resultado depois da pele).
    const fix = mesh.skeleton.bones.map((_, k) => {
      const common = skeleton.boneInverses[remap[k] ?? 0];
      const own = mesh.skeleton.boneInverses[k];
      if (!common || !own || common.equals(own)) return null;
      return common.clone().invert().multiply(own);
    });
    const fixNormal = fix.map((f) => (f ? new THREE.Matrix3().getNormalMatrix(f) : null));
    const fixLinear = fix.map((f) => (f ? new THREE.Matrix3().setFromMatrix4(f) : null));
    const tmp = new THREE.Vector3();
    const localMorph = morphs.map((name) => {
      const k = mesh.morphTargetDictionary?.[name];
      return k === undefined ? undefined : g.morphAttributes["position"]?.[k];
    });
    for (let v = 0; v < pos.count; v++) {
      const o = v0 + v;
      let main = 0;
      for (let c = 1; c < 4; c++) if (jw.getComponent(v, c) > jw.getComponent(v, main)) main = c;
      const k = ji.getComponent(v, main);
      const f = fix[k];
      const fn = fixNormal[k];
      tmp.set(pos.getX(v), pos.getY(v), pos.getZ(v));
      if (f) tmp.applyMatrix4(f);
      tmp.toArray(position, o * 3);
      if (nor) {
        tmp.set(nor.getX(v), nor.getY(v), nor.getZ(v));
        if (fn) tmp.applyMatrix3(fn).normalize();
        tmp.toArray(normal, o * 3);
      }
      if (col) color.set([col.getX(v), col.getY(v), col.getZ(v)], o * 3);
      for (let c = 0; c < 4; c++) {
        skinIndex[o * 4 + c] = remap[ji.getComponent(v, c)] ?? 0;
        skinWeight[o * 4 + c] = jw.getComponent(v, c);
      }
      part[o] = p;
      localMorph.forEach((attr, m) => {
        if (!attr) return;
        tmp.set(attr.getX(v), attr.getY(v), attr.getZ(v));
        const fl = fixLinear[k];
        if (fl) tmp.applyMatrix3(fl);
        tmp.toArray(morphDeltas[m] ?? new Float32Array(3), o * 3);
      });
    }
    if (g.index) for (let k = 0; k < g.index.count; k++) index[i0 + k] = g.index.getX(k) + v0;
    else for (let k = 0; k < pos.count; k++) index[i0 + k] = v0 + k;
    v0 += pos.count;
    i0 += g.index?.count ?? pos.count;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(position, 3));
  geometry.setAttribute("normal", new THREE.BufferAttribute(normal, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(color, 3));
  geometry.setAttribute("skinIndex", new THREE.Uint16BufferAttribute(skinIndex, 4));
  geometry.setAttribute("skinWeight", new THREE.BufferAttribute(skinWeight, 4));
  geometry.setAttribute("aPart", new THREE.BufferAttribute(part, 1));
  geometry.setIndex(new THREE.BufferAttribute(index, 1));
  geometry.morphAttributes["position"] = morphDeltas.map((d) => new THREE.BufferAttribute(d, 3));
  geometry.morphTargetsRelative = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return { geometry, morphs };
}
